import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { logger } from '@/lib/logger';
import { WebVitalInsertSchema } from '@/lib/webVitalsSchema';

type WebVitalsInsertRow = Database['public']['Tables']['web_vitals']['Insert'];

/**
 * Componente que monitora Core Web Vitals e reporta ao Sentry
 * Métricas rastreadas:
 * - LCP (Largest Contentful Paint): velocidade de carregamento
 * - INP (Interaction to Next Paint): responsividade (substituiu FID)
 * - CLS (Cumulative Layout Shift): estabilidade visual
 * - TTFB (Time to First Byte): tempo de resposta do servidor
 * - FCP (First Contentful Paint): primeira renderização
 * 
 * Targets Google recomendados:
 * - LCP: < 2.5s (bom), 2.5s-4s (ok), > 4s (ruim)
 * - INP: < 200ms (bom), 200ms-500ms (ok), > 500ms (ruim)
 * - CLS: < 0.1 (bom), 0.1-0.25 (ok), > 0.25 (ruim)
 */
export const WebVitalsReporter = () => {
  useEffect(() => {
    const userIdRef = { current: null as string | null };
    const persistedRef = { current: new Set<string>() };

    // Resolve user id once (avoid extra network per metric)
    supabase.auth
      .getUser()
      .then(({ data }) => {
        userIdRef.current = data.user?.id ?? null;
      })
      .catch(() => {
        userIdRef.current = null;
      });

    // Importa dinamicamente web-vitals (built-in no Vite)
    import('web-vitals').then(({ onCLS, onINP, onLCP, onFCP, onTTFB }) => {
      // Reporta cada métrica ao Sentry
      const sendToSentry = (metric: unknown) => {
        if (!metric || typeof metric !== 'object') return;
        const m = metric as { name?: unknown; value?: unknown; rating?: unknown; delta?: unknown; navigationType?: unknown };
        const name = typeof m.name === 'string' ? m.name : undefined;
        const value = typeof m.value === 'number' ? m.value : undefined;
        const rating = typeof m.rating === 'string' ? m.rating : undefined;
        const delta = typeof m.delta === 'number' ? m.delta : undefined;
        const navigationType = typeof m.navigationType === 'string' ? m.navigationType : undefined;

        if (!name || value === undefined || !rating) return;
        
        // Determina se a métrica está "boa", "ok" ou "ruim"
        const isPoor = rating === 'poor';
        // Decisão de produto: registrar tudo (good + needs-improvement + poor)
        const shouldPersist = true;

        // Evita duplicar persistência em DEV (StrictMode dispara effects 2x)
        const dedupeKey = `${window.location.pathname}|${name}|${navigationType ?? 'unknown'}`;
        
        // Envia medição para Sentry Performance
        // CLS é unitless (ratio); as demais são tempo (ms).
        Sentry.setMeasurement(name, value, name === 'CLS' ? 'none' : 'millisecond');

        // Vitals ruins viram BREADCRUMB (contexto), não issue.
        // Persistência em `web_vitals` (abaixo) continua sendo a fonte para análise.
        if (isPoor) {
          Sentry.addBreadcrumb({
            category: 'web-vital',
            level: 'warning',
            message: `Poor Web Vital: ${name}`,
            data: {
              value,
              rating,
              threshold: getThreshold(name),
              page: window.location.pathname,
            },
            timestamp: Date.now() / 1000,
          });
        }

        // Persistência (admin-only read via RLS)
        const userId = userIdRef.current;
        if (userId && shouldPersist && !persistedRef.current.has(dedupeKey)) {
          persistedRef.current.add(dedupeKey);
          let insertRow: WebVitalsInsertRow | null = null;
          try {
            const parsed = WebVitalInsertSchema.parse({
              user_id: userId,
              path: window.location.pathname,
              metric_name: name,
              value,
              rating,
              delta,
              navigation_type: navigationType,
            });

            // Zod garante o shape; convertemos para o tipo esperado pelo client tipado.
            insertRow = parsed as unknown as WebVitalsInsertRow;
          } catch {
            insertRow = null;
          }

          if (insertRow) {
            const persist = async () => {
              try {
                const { error } = await supabase
                  .from('web_vitals')
                  .insert([insertRow]);

                if (error) {
                  logger.warn('[WebVitalsReporter] insert failed', {
                    metric: name,
                    message: error.message,
                  });
                }
              } catch {
                // swallow
              }
            };

            void persist();
          }
        }

        logger.debug(`[WebVitalsReporter] ${name}`, { value, rating, path: window.location.pathname });
      };
      
      // Registra listeners para cada métrica
      onCLS(sendToSentry);
      onINP(sendToSentry); // Substituiu FID na web-vitals v4
      onLCP(sendToSentry);
      onFCP(sendToSentry);
      onTTFB(sendToSentry);
    });
  }, []);
  
  return null; // Componente invisível
};

// Helper: retorna o threshold "bom" para cada métrica
const getThreshold = (metric: string): string => {
  const thresholds: Record<string, string> = {
    'LCP': '2.5s',
    'INP': '200ms', // Substituiu FID
    'CLS': '0.1',
    'FCP': '1.8s',
    'TTFB': '600ms',
  };
  return thresholds[metric] || 'N/A';
};
